import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Empty, Input, Spin, Tag } from 'antd';
import { SearchOutlined, TagOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from "react-router-dom";
import apiClient from 'services/apiClient';
import { useMyContext } from 'Context/MyContextProvider';
// import { FixedSizeList as List } from 'react-window';
import { capitalize, debounce } from 'lodash';
import { motion} from 'framer-motion';
import BadgeSelector from './BadgeSelector';
import { useLiveChatContext } from './LiveChatContext';
import ChatMenuItem from './ChatMenuItem';
import { getChatMenuPreviewText, matchesChatFilter } from './chatUtils';

// Removed framer-motion listVariants and itemVariants for a snappier list
const ChatMenu = (props) => {
	const { api, UserData, isMobile } = useMyContext();
	const {
		chatList,
		setChatList,
		loadingChats,
		loadingMoreChats,
		hasMoreChats,
		fetchInitialChats,
		fetchMoreChats,
		searchChats,
		chatFilter,
		crossFilterNew,
		markRead,
		setActiveContact,
	} = useLiveChatContext();
	const location = useLocation();
	const navigate = useNavigate();
	const { setMenuState, onChatOpen } = props
	const id = parseInt(location.pathname.match(/\/([^/]+)\/?$/)[1])
	const [listHeight, setListHeight] = useState();
	const [allBadges, setAllBadges] = useState([]);
	const [badgesLoading, setBadgesLoading] = useState(false);

	const parentRef = useRef();

	const [searchQuery, setSearchQuery] = useState('');
	const [isFiltering, setIsFiltering] = useState(false);

	const fetchAllBadges = useCallback(async (forceRefresh = false, chatId = null, updatedBadges = null) => {
		setBadgesLoading(true);
		try {
			// Fetch all badges
			const response = await apiClient.get(`${api}user/badges`);

			if (response.data.status) {
				setAllBadges(response.data.badges || []);
				if (chatId && updatedBadges !== null) {
					setChatList(prevList =>
						prevList.map(chat =>
							chat.id === chatId
								? { ...chat, badges: updatedBadges }
								: chat
						)
					);
				}
			}
		} catch (error) {
			console.error("Error fetching badges:", error);
		} finally {
			setBadgesLoading(false);
		}
	}, [api, UserData, setChatList]);

	useEffect(() => {
		if (isMobile) {
			return undefined;
		}

		const calculateHeight = () => {
			const chatMenu = document.querySelector('.chat-menu');
			const toolbar = chatMenu?.querySelector('.chat-menu-toolbar');
			if (chatMenu && toolbar && chatMenu.clientHeight > 0) {
				setListHeight(chatMenu.clientHeight - toolbar.offsetHeight);
				return;
			}
			setListHeight(500);
		};

		calculateHeight();
		window.addEventListener('resize', calculateHeight);
		return () => window.removeEventListener('resize', calculateHeight);
	}, [isMobile]);

	useEffect(() => {
		fetchAllBadges();
	}, [fetchAllBadges])

	const [showTag, setShowTag] = useState({});
	const [showFilters, setShowFilters] = useState(false);
	const [searchActive, setSearchActive] = useState(false);
	const [activeBadgeFilter, setActiveBadgeFilter] = useState(null);

	const handleFilterClick = useCallback((filterKey) => {
		setActiveBadgeFilter(null);
		setSearchQuery('');
		setIsFiltering(true);
		fetchInitialChats(filterKey).finally(() => setIsFiltering(false));
	}, [fetchInitialChats]);

	const runSearch = useCallback((query) => {
		const trimmed = String(query || '').trim();
		setSearchQuery(trimmed);
		setIsFiltering(true);

		const request = trimmed
			? searchChats(trimmed)
			: fetchInitialChats(chatFilter);

		request.finally(() => setIsFiltering(false));
	}, [searchChats, fetchInitialChats, chatFilter]);

	const debouncedSearch = useMemo(
		() => debounce((query) => runSearch(query), 300),
		[runSearch]
	);

	useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

	const openChat = useCallback((contactId) => {
		setActiveContact(String(contactId));
		markRead(contactId);
		setMenuState(contactId);
		navigate(`${contactId}`);
		if (onChatOpen) {
			onChatOpen();
		}
	}, [setActiveContact, markRead, setMenuState, navigate, onChatOpen]);
	const searchOnChange = (e) => {
		const query = e.target.value;
		setIsFiltering(true);
		debouncedSearch(query);
	};



	const getSubTitle = useCallback((message) => getChatMenuPreviewText(message), []);

	const filters = [
		{ key: 'active', label: 'Active', color: 'green' },
		{ key: 'expired', label: 'Expired', color: 'red' },
	];

	// Toggle tag visibility function
	const toggleTagVisibility = useCallback((itemId) => {
		setShowTag(prev => ({
			...prev,
			[itemId]: !prev[itemId]
		}));
	}, []);

	const displayedChats = useMemo(() => {
		let items = chatList.filter((chat) => matchesChatFilter(chat, chatFilter));

		if (activeBadgeFilter) {
			items = items.filter((item) =>
				item.badges?.some((badge) => badge.id === activeBadgeFilter.id && badge.assigned)
			);
		}

		return items;
	}, [chatList, chatFilter, activeBadgeFilter]);

	useEffect(() => {
		if (!isMobile || loadingChats) {
			return undefined;
		}

		const resetListScroll = () => {
			if (parentRef.current) {
				parentRef.current.scrollTop = 0;
			}
		};

		resetListScroll();

		return undefined;
	}, [isMobile, loadingChats, chatFilter]);

	const HandleBadgeClick = (badge) => {
		if (activeBadgeFilter && activeBadgeFilter.id === badge.id) {
			setActiveBadgeFilter(null);
			return;
		}

		setSearchQuery('');
		setActiveBadgeFilter(badge);
	};


	return (
		<div
			className={`chat-menu${isMobile ? ' chat-menu-mobile' : ''}`}
		>
			{/* ── Toolbar row ──────────────────────────────────── */}
			<div
				className="d-flex align-items-center chat-menu-toolbar"
				style={{
					padding: '6px 10px',
					gap: '6px',
					overflow: 'hidden',
					flexShrink: 0,
				}}
			>
				{/* Search expand area */}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						flex: searchActive ? 1 : 'none',
						transition: 'flex 0.3s ease',
						overflow: 'hidden',
					}}
				>
					{/* Search icon button (collapsed) */}
					{!searchActive && (
						<Button
							type="text"
							icon={<SearchOutlined style={{ fontSize: '16px', color: '#6b6b83' }} />}
							onClick={() => setSearchActive(true)}
							style={{
								height: '32px',
								width: '32px',
								borderRadius: '8px',
								background: '#f4f4f9',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								padding: 0,
								flexShrink: 0,
							}}
						/>
					)}

					{/* Expanded search input */}
					{searchActive && (
						<>
							<Input
								autoFocus
								placeholder="Search"
								onChange={searchOnChange}
								prefix={<SearchOutlined style={{ fontSize: '13px', color: '#8c8c8c' }} />}
								className="chat-menu-search-input"
								style={{
									flex: 1,
									borderRadius: '1rem',
									border: '1px solid #e6ebf1',
									height: '36px',
									fontSize: '13px',
								}}
							/>
							<Button
								type="text"
								icon={<CloseOutlined style={{ fontSize: '13px', color: '#6b6b83' }} />}
								onClick={() => {
									setSearchActive(false);
									setSearchQuery('');
									setIsFiltering(true);
									fetchInitialChats(chatFilter).finally(() => setIsFiltering(false));
								}}
								style={{
									marginLeft: '6px',
									height: '36px',
									width: '36px',
									borderRadius: '1rem',
									background: '#f4f4f9',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									padding: 0,
									flexShrink: 0,
								}}
							/>
						</>
					)}
				</div>

				{/* Active / Expired filter tags — hidden while searching */}
				{!searchActive && (
					<div
						className="d-flex align-items-center"
						style={{ flex: 1, gap: '6px', justifyContent: 'center' }}
					>
						{filters?.map(({ key, label, color }) => {
							const showNewHint = crossFilterNew?.[key] && chatFilter !== key;
							const hintTone = key === 'active'
								? { border: '1px solid #52c41a', text: '#389e0d', dot: '#52c41a' }
								: { border: '1px solid #ff4d4f', text: '#cf1322', dot: '#ff4d4f' };

							return (
							<motion.div
								key={key}
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
							>
								<Tag
									className="p-0 px-2 m-0"
									color={chatFilter === key ? color : ''}
									onClick={() => handleFilterClick(key)}
									style={{
										cursor: 'pointer',
										display: 'inline-flex',
										alignItems: 'center',
										gap: 6,
										...(showNewHint
											? {
												border: hintTone.border,
												color: hintTone.text,
											}
											: {}),
									}}
								>
									<span style={{ color: showNewHint ? hintTone.text : undefined }}>
										{label}
									</span>
									{showNewHint && (
										<span
											style={{
												display: 'inline-flex',
												alignItems: 'center',
												gap: 4,
												fontSize: 11,
												fontWeight: 600,
												color: hintTone.text,
											}}
										>
											<span
												style={{
													width: 7,
													height: 7,
													borderRadius: '50%',
													backgroundColor: hintTone.dot,
													flexShrink: 0,
												}}
											/>
											New
										</span>
									)}
								</Tag>
							</motion.div>
							);
						})}
					</div>
				)}

				{/* Tag (badge filter) button — hidden while searching */}
				{!searchActive && (
					<Button
						type="text"
						icon={<TagOutlined style={{ fontSize: '16px', color: '#6b6b83' }} />}
						onClick={() => setShowFilters(!showFilters)}
						style={{
							height: '32px',
							width: '32px',
							borderRadius: '8px',
							background: '#f4f4f9',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							padding: 0,
							flexShrink: 0,
						}}
					/>
				)}
			</div>

			{/* ── Badge selector (tag filter) ───────────────────── */}
			{showFilters && (
				<motion.div
					className={isMobile ? 'chat-menu-filters' : undefined}
					initial={{ opacity: 0, height: 0 }}
					animate={{ opacity: 1, height: 'auto' }}
					exit={{ opacity: 0, height: 0 }}
					transition={{ duration: 0.3 }}
				>
					<div className="chat-menu-toolbar d-flex justify-content-center align-items-center mx-2">
						<BadgeSelector
							list={chatList}
							allBadges={allBadges}
							onBadgesUpdate={(forceRefresh = false, chatId = null, updatedBadges = null) =>
								fetchAllBadges(forceRefresh, chatId, updatedBadges)
							}
							loading={badgesLoading}
							setList={setChatList}
							isVisible={showFilters}
							HandleBadgeClick={HandleBadgeClick}
						/>
					</div>
				</motion.div>
			)}
			<div
				className="chat-menu-list"
				ref={parentRef}
				id="chatScrollableDiv"
				style={isMobile ? undefined : {
					overflow: 'auto',
					height: listHeight,
				}}
			>
				{loadingChats || isFiltering ? (
					<div className="text-center">
						<Spin size='medium' />
					</div>
				) : (
					displayedChats?.length > 0 ? (
						<>
							<ul
								className="chat-list-motion-ul"
								style={{
									padding: 0,
									margin: 0,
									listStyle: 'none',
									position: 'relative'
								}}
							>
								{displayedChats.map((item, index) => {
									const itemId = item?.id ?? `fallback-id-${index}`;
									const stableKey = item?.number || item?.id || `chat-${index}`;

									return (
										<ChatMenuItem
											key={stableKey}
											item={item}
											index={index}
											isLast={index === displayedChats?.length - 1}
											isSelected={item?.id === id}
											openChat={openChat}
											getSubTitle={getSubTitle}
											showTag={showTag}
											toggleTagVisibility={toggleTagVisibility}
											allBadges={allBadges}
											setChatList={setChatList}
											fetchAllBadges={fetchAllBadges}
										/>
									);
								})}
							</ul>
							{hasMoreChats && !activeBadgeFilter && (
								<div className="text-center py-3">
									<Button
										type="default"
										onClick={fetchMoreChats}
										loading={loadingMoreChats}
										disabled={loadingMoreChats}
									>
										Load more
									</Button>
								</div>
							)}
						</>
					)
						: (
							<Empty
								className='mt-5'
								description={
									<span>
										{activeBadgeFilter
											? `No ${capitalize(chatFilter)} chats found with badge "${activeBadgeFilter.name}"`
											: searchQuery
												? `No ${capitalize(chatFilter)} chats found for "${searchQuery}"`
												: `No ${capitalize(chatFilter)} chats found`
										}
									</span>
								}
							/>
						)
				)}
			</div>
		</div>
	)
}

export default ChatMenu
